<template>
  <div class="space-y-3">
    <!-- Create Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="bg-gray-50 border-b border-gray-300 px-4 py-2.5 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">Add Programmes</h3>
        <span class="text-xs text-gray-400">Fill the programme details once, then add each record to the batch queue.</span>
      </div>

      <div class="p-3 space-y-3">
        <!-- Faculty & Department selectors -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Faculty *</label>
            <Select
              v-model="selectedFacultyId"
              :options="faculties"
              optionLabel="faculty_name"
              optionValue="id"
              placeholder="Select a faculty"
              class="w-full"
              filter
              data-testid="programme-faculty-select"
              @show="refreshFaculties"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Department *</label>
            <Select
              v-model="departmentId"
              :options="departmentsList"
              optionLabel="department_name"
              optionValue="id"
              placeholder="Select a department"
              class="w-full"
              :disabled="!selectedFacultyId"
              filter
              data-testid="programme-department-select"
            />
          </div>
        </div>

        <!-- Programme Fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Programme Name *</label>
            <InputText
              v-model="form.program_name"
              class="w-full"
              placeholder="e.g. Computer Science"
              :class="{ 'p-invalid': getErrorMessage('program_name') }"
              data-testid="programme-name-input"
            />
            <small v-if="getErrorMessage('program_name')" class="text-red-500">
              {{ getErrorMessage('program_name') }}
            </small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Programme Code *</label>
            <InputText
              v-model="form.prog_code"
              class="w-full"
              placeholder="e.g. CSC"
              :class="{ 'p-invalid': getErrorMessage('prog_code') }"
              data-testid="programme-code-input"
            />
            <small v-if="getErrorMessage('prog_code')" class="text-red-500">
              {{ getErrorMessage('prog_code') }}
            </small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Type *</label>
            <Select
              v-model="form.type"
              :options="typeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Type"
              class="w-full"
              data-testid="programme-type-select"
              @change="handleTypeChange"
            />
            <div v-if="showAddTypeInput" class="flex gap-1.5 mt-0.5">
              <InputText v-model="newType" class="flex-1" placeholder="Enter new type" data-testid="programme-new-type-input" />
              <Button label="Add" size="small" data-testid="programme-confirm-type-btn" @click="addNewType" />
              <Button icon="pi pi-times" severity="secondary" text size="small" data-testid="programme-cancel-type-btn" @click="showAddTypeInput = false" />
            </div>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Duration (semesters) *</label>
            <InputNumber v-model="form.duration" class="w-full" placeholder="e.g. 8" :min="1" data-testid="programme-duration-input" />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Degree *</label>
            <Select
              v-model="form.degree"
              :options="degreeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Degree"
              class="w-full"
              data-testid="programme-degree-select"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Graduation minimum units *</label>
            <InputNumber v-model="form.min_units" class="w-full" placeholder="e.g. 120" :min="0" data-testid="programme-min-units-input" />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Calculation Method</label>
            <Select
              v-model="form.calculation_method"
              :options="calculationMethodOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Method"
              class="w-full"
              showClear
              data-testid="programme-calc-method-select"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Delivery Mode</label>
            <Select
              v-model="form.delivery_mode"
              :options="deliveryModeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Regular (default)"
              class="w-full"
              showClear
              data-testid="programme-delivery-mode-select"
              @change="onDeliveryModeChange"
            />
            <small class="text-xs text-gray-500">
              Leave on Regular unless this programme teaches in sequential time-boxed blocks
              (e.g. one course per month, or short residencies). Choosing Modular or Residence
              gates registration behind modules you define in the Modules step.
            </small>
          </div>
          <RegistrationUnitLimitFields class="md:col-span-2" v-model:minimum="form.min_units_per_registration"
              v-model:maximum="form.max_units_per_registration" scope="Programme" id-prefix="programme-create"
              :minimum-error="getErrorMessage('min_units_per_registration')"
              :maximum-error="getErrorMessage('max_units_per_registration')" />
          <div v-if="formUsesModules" class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Module Results</label>
            <Select
              v-model="form.module_result_mode"
              :options="moduleResultModeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Roll up into semester (default)"
              class="w-full"
              showClear
              data-testid="programme-result-mode-select"
            />
            <small class="text-xs text-gray-500">
              Roll up keeps courses together in the semester report. Separate groups courses under module
              headings on supported results and transcripts; semester totals and approval remain shared.
            </small>
          </div>
          <div v-if="showProgressionType" class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Progression Type</label>
            <Select
              v-model="form.progression_type"
              :options="progressionTypeOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              data-testid="programme-progression-type-select"
            />
            <small class="text-xs text-gray-500">
              Year-based keeps the level fixed (e.g. 800) and uses cumulative semesters (1, 2, 3, 4).
            </small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Outstanding Mode</label>
            <Select
              v-model="form.outstanding_mode"
              :options="outstandingModeOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              data-testid="programme-outstanding-mode-select"
            />
            <small class="text-xs text-gray-500">
              Controls when failed courses reappear as outstanding.
            </small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Compulsory Mode</label>
            <Select
              v-model="form.compulsory_mode"
              :options="compulsoryModeOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              data-testid="programme-compulsory-mode-select"
            />
            <small class="text-xs text-gray-500">
              Flexible lets students choose courses freely within unit limits.
            </small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Enforce Prerequisites</label>
            <div class="flex items-center gap-2 mt-1">
              <Checkbox v-model="form.enforce_prerequisites" binary inputId="create-enforce-prereqs" data-testid="programme-enforce-prereqs-checkbox" />
              <label for="create-enforce-prereqs" class="text-sm text-gray-600">Block registration for unmet prerequisites</label>
            </div>
          </div>
        </div>

        <Button
          label="Add Programme"
          icon="pi pi-plus"
          size="small"
          :disabled="!canAddProgramme"
          data-testid="programme-add-btn"
          @click="addProgramme"
        />

        <!-- Pending Programmes Table -->
        <div v-if="pendingProgrammes.length">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-xs font-medium text-gray-700">
              Queued
              <span class="inline-flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 ml-1">
                {{ pendingProgrammes.length }}
              </span>
            </span>
            <Button
              v-if="pendingProgrammes.length > 1"
              label="Clear All"
              severity="danger"
              text
              size="small"
              data-testid="programme-clear-pending-btn"
              @click="clearItems"
            />
          </div>

          <DataTable :value="pendingProgrammes" size="small" stripedRows>
            <Column header="#">
              <template #body="{ index }">
                <span class="text-gray-400 font-mono text-xs">{{ index + 1 }}</span>
              </template>
            </Column>
            <Column field="program_name" header="Name" />
            <Column field="prog_code" header="Code" />
            <Column field="type" header="Type" />
            <Column field="degree" header="Degree" />
            <Column header="Duration (sem)">
              <template #body="{ data }">{{ data.duration }} sem</template>
            </Column>
            <Column header="Min Units">
              <template #body="{ data }">{{ data.min_units }}</template>
            </Column>
            <Column header="Calc. Method">
              <template #body="{ data }">{{ data.calculation_method || '—' }}</template>
            </Column>
            <Column v-if="pendingErrors.some(Boolean)" header="Not created">
              <template #body="{ index }">
                <small v-if="pendingErrors[index]" class="text-red-500" :data-testid="`programme-pending-error-${index}`">
                  {{ pendingErrors[index] }}
                </small>
              </template>
            </Column>
            <Column>
              <template #body="{ index }">
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Remove'"
                  :data-testid="`programme-remove-pending-${index}`"
                  @click="removeProgramme(index)"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <div class="flex justify-end pt-1.5 border-t border-gray-300">
          <Button
            label="Submit All Programmes"
            icon="pi pi-check"
            size="small"
            severity="success"
            :loading="isSubmitting"
            :disabled="pendingProgrammes.length === 0"
            data-testid="programme-submit-all-btn"
            @click="submitAll"
          />
        </div>
      </div>
    </div>

    <!-- Existing Programmes Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-2.5 border-b border-gray-300 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">
          Existing Programmes
          <span v-if="paginatorInfo?.total" class="text-xs font-normal text-gray-400 ml-1">
            ({{ paginatorInfo.total }})
          </span>
        </h3>
      </div>

      <div class="p-0">
        <div v-if="loadingSchoolProgramme" class="flex items-center justify-center py-8">
          <span class="pi pi-spin pi-spinner text-2xl text-primary"></span>
        </div>

        <DataTable
          v-else-if="programmes.length"
          :value="programmes"
          size="small"
          stripedRows
          :paginator="true"
          :rows="rowsPerPage"
          :totalRecords="paginatorInfo?.total ?? 0"
          :first="firstRecord"
          :lazy="true"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 25, 50]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          @page="onPageChange"
        >
          <Column field="program_name" header="Programme" />
          <Column field="prog_code" header="Code" />
          <Column field="type" header="Type" />
          <Column field="degree" header="Degree" />
          <Column header="Duration">
            <template #body="{ data }">{{ data.duration }}</template>
          </Column>
          <Column header="Min Units">
            <template #body="{ data }">{{ data.min_units }}</template>
          </Column>
          <Column header="Calc. Method">
            <template #body="{ data }">{{ data.calculation_method || '—' }}</template>
          </Column>
          <Column header="Actions">
            <template #body="{ data }">
              <div class="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-secondary p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Edit'"
                  :data-testid="`programme-edit-${data.id}`"
                  @click="openEdit(data)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Delete'"
                  :loading="deletingId === data.id"
                  :data-testid="`programme-delete-${data.id}`"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-else class="py-6 text-center text-gray-400 text-xs">No programmes created yet</div>
      </div>
    </div>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEdit" modal header="Edit Programme" :style="{ width: '36rem' }">
      <!-- Faculty & Department selectors -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Faculty</label>
          <Select
            v-model="editFacultyId"
            :options="faculties"
            optionLabel="faculty_name"
            optionValue="id"
            placeholder="Select a faculty"
            class="w-full"
            filter
            data-testid="programme-edit-faculty-select"
            @show="refreshFaculties"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Department</label>
          <Select
            v-model="editForm.department_id"
            :options="editDepartmentsList"
            optionLabel="department_name"
            optionValue="id"
            placeholder="Select a department"
            class="w-full"
            :disabled="!editFacultyId"
            filter
            data-testid="programme-edit-department-select"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Programme Name</label>
          <InputText
            v-model="editForm.program_name"
            :class="{ 'p-invalid': getErrorMessage('program_name') }"
            data-testid="programme-edit-name-input"
          />
          <small v-if="getErrorMessage('program_name')" class="text-red-500">
            {{ getErrorMessage('program_name') }}
          </small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Programme Code</label>
          <InputText
            v-model="editForm.prog_code"
            :class="{ 'p-invalid': getErrorMessage('prog_code') }"
            data-testid="programme-edit-code-input"
          />
          <small v-if="getErrorMessage('prog_code')" class="text-red-500">
            {{ getErrorMessage('prog_code') }}
          </small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Degree</label>
          <Select v-model="editForm.degree" :options="degreeOptions" optionLabel="label" optionValue="value" class="w-full" data-testid="programme-edit-degree-select" />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Type</label>
          <InputText v-model="editForm.type" data-testid="programme-edit-type-input" />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Duration (semesters)</label>
          <InputNumber v-model="editForm.duration" :min="1" data-testid="programme-edit-duration-input" />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Min Units</label>
          <InputNumber v-model="editForm.min_units" :min="0" data-testid="programme-edit-min-units-input" />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Calculation Method</label>
          <Select
            v-model="editForm.calculation_method"
            :options="calculationMethodOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Method"
            class="w-full"
            showClear
            data-testid="programme-edit-calc-method-select"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Delivery Mode</label>
          <Select
            v-model="editForm.delivery_mode"
            :options="deliveryModeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Regular (default)"
            class="w-full"
            data-testid="programme-edit-delivery-mode-select"
          />
        </div>
        <RegistrationUnitLimitFields class="md:col-span-2" v-model:minimum="editForm.min_units_per_registration"
              v-model:maximum="editForm.max_units_per_registration" scope="Programme" id-prefix="programme-edit"
              :minimum-error="getErrorMessage('min_units_per_registration')"
              :maximum-error="getErrorMessage('max_units_per_registration')" />
          <div v-if="editUsesModules" class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Module Results</label>
          <Select
            v-model="editForm.module_result_mode"
            :options="moduleResultModeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Roll up into semester (default)"
            class="w-full"
            data-testid="programme-edit-result-mode-select"
          />
            <small class="text-xs text-gray-500">
              Roll up keeps courses together in the semester report. Separate groups courses under module
              headings on supported results and transcripts; semester totals and approval remain shared.
            </small>
        </div>
        <div v-if="showEditProgressionType" class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Progression Type</label>
          <Select
            v-model="editForm.progression_type"
            :options="progressionTypeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="programme-edit-progression-type-select"
          />
          <small class="text-xs text-gray-500">
            Year-based keeps the level fixed and uses cumulative semesters (1, 2, 3, 4).
          </small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Outstanding Mode</label>
          <Select
            v-model="editForm.outstanding_mode"
            :options="outstandingModeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="programme-edit-outstanding-mode-select"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Compulsory Mode</label>
          <Select
            v-model="editForm.compulsory_mode"
            :options="compulsoryModeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="programme-edit-compulsory-mode-select"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Enforce Prerequisites</label>
          <div class="flex items-center gap-2 mt-1">
            <Checkbox v-model="editForm.enforce_prerequisites" binary inputId="edit-enforce-prereqs" data-testid="programme-edit-enforce-prereqs-checkbox" />
            <label for="edit-enforce-prereqs" class="text-sm text-gray-600">Block registration for unmet prerequisites</label>
          </div>
        </div>
      </div>

      <!-- Graduation Criteria -->
      <div class="mt-5 pt-4 border-t border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <label class="text-sm font-medium text-gray-700">Graduation Criteria</label>
          <Select
            v-model="selectedPreset"
            :options="presetOptions"
            optionLabel="label"
            optionValue="key"
            placeholder="Load preset..."
            class="w-48"
            size="small"
            showClear
            data-testid="programme-edit-criteria-preset"
            @change="applyPreset"
          />
        </div>
        <p class="text-xs text-gray-400 mb-3">Configure which checks are run when evaluating graduation eligibility for students in this programme.</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="flex items-center gap-2">
            <Checkbox v-model="editCriteria.program_completion" binary inputId="ec-prog-completion" data-testid="programme-edit-check-program-completion" />
            <label for="ec-prog-completion" class="text-sm text-gray-600">Programme Duration Check</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="editCriteria.unit_validation" binary inputId="ec-unit-val" data-testid="programme-edit-check-unit-validation" />
            <label for="ec-unit-val" class="text-sm text-gray-600">Minimum Units Check</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="editCriteria.cgpa_validation" binary inputId="ec-cgpa-val" data-testid="programme-edit-check-cgpa-validation" />
            <label for="ec-cgpa-val" class="text-sm text-gray-600">CGPA / Weighted Avg Check</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="editCriteria.course_completion" binary inputId="ec-course-comp" data-testid="programme-edit-check-course-completion" />
            <label for="ec-course-comp" class="text-sm text-gray-600">Course Completion Check</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="editCriteria.clearance" binary inputId="ec-clearance" data-testid="programme-edit-check-clearance" />
            <label for="ec-clearance" class="text-sm text-gray-600">Clearance Check</label>
          </div>
        </div>

        <!-- Thresholds (only show when relevant checks are enabled) -->
        <div v-if="editCriteria.cgpa_validation || editCriteria.unit_validation" class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div v-if="editCriteria.cgpa_validation && editForm.calculation_method !== 'weighted'" class="flex flex-col gap-1">
            <label class="text-xs text-gray-500">Min CGPA</label>
            <InputNumber v-model="editCriteria.min_cgpa" class="w-full" :min="0" :max="5" :maxFractionDigits="2" placeholder="e.g. 1.0" data-testid="programme-edit-min-cgpa" />
          </div>
          <div v-if="editCriteria.cgpa_validation && editForm.calculation_method === 'weighted'" class="flex flex-col gap-1">
            <label class="text-xs text-gray-500">Min Weighted Avg</label>
            <InputNumber v-model="editCriteria.min_weighted_avg" class="w-full" :min="0" :max="100" :maxFractionDigits="2" placeholder="e.g. 40" data-testid="programme-edit-min-weighted-avg" />
          </div>
          <div v-if="editCriteria.unit_validation" class="flex flex-col gap-1">
            <label class="text-xs text-gray-500">Min Units Override</label>
            <InputNumber v-model="editCriteria.min_units" class="w-full" :min="0" placeholder="Uses programme default" data-testid="programme-edit-criteria-min-units" />
          </div>
        </div>

        <!-- Grading type -->
        <div class="mt-3">
          <label class="text-xs text-gray-500 block mb-1">Grading Type</label>
          <div class="flex gap-4">
            <div class="flex items-center gap-2">
              <RadioButton v-model="editCriteria.grading_type" inputId="gt-numeric" value="numeric" data-testid="programme-edit-grading-numeric" />
              <label for="gt-numeric" class="text-sm text-gray-600">Numeric (CGPA / Weighted)</label>
            </div>
            <div class="flex items-center gap-2">
              <RadioButton v-model="editCriteria.grading_type" inputId="gt-passfail" value="pass_fail" data-testid="programme-edit-grading-passfail" />
              <label for="gt-passfail" class="text-sm text-gray-600">Pass / Fail</label>
            </div>
          </div>
        </div>
      </div>

      <!-- Default Transcript Template -->
      <div class="mt-5 pt-4 border-t border-gray-200">
        <label class="text-sm font-medium text-gray-700 block mb-1">Default Transcript Template</label>
        <p class="text-xs text-gray-400 mb-3">
          Choose a transcript template that will be used by default when generating transcripts for students in this programme.
        </p>
        <div class="flex items-end gap-2">
          <div class="flex-1">
            <Select
              v-model="editTranscriptTemplateId"
              :options="transcriptTemplateOptions"
              optionLabel="name"
              optionValue="id"
              placeholder="No template assigned"
              class="w-full"
              showClear
              filter
              :loading="loadingTranscriptTemplates"
              data-testid="programme-edit-transcript-template-select"
            />
          </div>
          <Button
            label="Save Template"
            icon="pi pi-check"
            size="small"
            :loading="savingTranscriptTemplate"
            :disabled="editTranscriptTemplateId === originalTranscriptTemplateId"
            data-testid="programme-edit-save-transcript-template-btn"
            @click="saveTranscriptTemplate"
          />
        </div>
      </div>

      <template #footer>
        <Button label="Cancel" icon="pi pi-times" text size="small" data-testid="programme-edit-cancel-btn" @click="showEdit = false" />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="updatingProgramme"
          :disabled="!editForm.program_name?.trim()"
          data-testid="programme-edit-save-btn"
          @click="submitEdit"
        />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import RegistrationUnitLimitFields from '@/components/common/RegistrationUnitLimitFields.vue';
import { registrationUnitLimitError } from '@/utils/registrationUnitLimits';
import { useFormErrors } from '@/composables/useFormErrors';
import { submissionToast, useTertiaryEntityWorkflow } from '@/composables/useTertiaryEntityWorkflow';
import { useGetGraduationCriteriaPresetsQuery } from '@/types/vue-apollo-helpers';
import type { ProgrammeInput, ProgrammeType, ModuleResultModeEnum } from '@/types/composition-functions';
import { ProgrammeDeliveryModeEnum, ProgressionTypeEnum, OutstandingModeEnum, CompulsoryModeEnum } from '@/types/composition-functions';
import {
  DEGREE_OPTIONS,
  CALCULATION_METHOD_OPTIONS,
  DELIVERY_MODE_OPTIONS,
  MODULE_RESULT_MODE_OPTIONS,
  PROGRESSION_TYPE_OPTIONS,
  OUTSTANDING_MODE_OPTIONS,
  COMPULSORY_MODE_OPTIONS,
  POSTGRAD_DEGREES,
} from '@/constants/programme-options';
import { GET_TRANSCRIPT_TEMPLATES } from '@/graphql/transcript/queries';
import { baseProgCode } from '@/utils/progCode';
import { SET_PROGRAMME_TRANSCRIPT_TEMPLATE_DEFAULT } from '@/graphql/transcript/mutations';

const toast = useToast();
const confirm = useConfirm();
const {
  createTertiaryProgramme,
  updateTertiaryProgramme,
  deleteTertiaryProgramme,
  updatingProgramme,
} = useTertiaryMutations();
const {
  programmes,
  paginatorInfo,
  loadingSchoolProgramme,
  refetchSchoolProgramme,
  setPage,
  setItemsPerPage,
} = useProgrammes({ autoFetch: true, first: 200, page: 1 });
const { faculties, refreshFaculties } = useFaculties();
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

// ─── Form state for batch ────────────────────────────────────────
const selectedFacultyId = ref('');
const departmentId = ref('');
const { pendingItems: pendingProgrammes, pendingErrors, isSubmitting, addItem, removeItem, clearItems, submitPending } = useTertiaryEntityWorkflow<ProgrammeInput>();
const form = reactive({
  min_units_per_registration: null as number | null,
  max_units_per_registration: null as number | null,
  program_name: '',
  prog_code: '',
  type: '',
  duration: null as number | null,
  min_units: null as number | null,
  degree: '',
  calculation_method: null as string | null,
  delivery_mode: null as ProgrammeDeliveryModeEnum | null,
  module_result_mode: null as ModuleResultModeEnum | null,
  progression_type: ProgressionTypeEnum.Level as ProgressionTypeEnum,
  outstanding_mode: OutstandingModeEnum.SemesterMatch as OutstandingModeEnum,
  compulsory_mode: CompulsoryModeEnum.Enforced as CompulsoryModeEnum,
  enforce_prerequisites: false,
});
const departmentsList = computed(() => {
  const faculty = faculties.value.find(f => f.id === selectedFacultyId.value);
  return faculty?.departments ?? [];
});

const degreeOptions = DEGREE_OPTIONS;
const calculationMethodOptions = CALCULATION_METHOD_OPTIONS;
const deliveryModeOptions = DELIVERY_MODE_OPTIONS;
const moduleResultModeOptions = MODULE_RESULT_MODE_OPTIONS;
const progressionTypeOptions = PROGRESSION_TYPE_OPTIONS;
const outstandingModeOptions = OUTSTANDING_MODE_OPTIONS;
const compulsoryModeOptions = COMPULSORY_MODE_OPTIONS;

const showProgressionType = computed(() => POSTGRAD_DEGREES.has(form.degree));
const showEditProgressionType = computed(() => POSTGRAD_DEGREES.has(editForm.degree));

const usesModules = (mode: ProgrammeDeliveryModeEnum | null) =>
  mode === ProgrammeDeliveryModeEnum.Modular || mode === ProgrammeDeliveryModeEnum.Residence;
const formUsesModules = computed(() => usesModules(form.delivery_mode));

function onDeliveryModeChange() {
  if (!formUsesModules.value) form.module_result_mode = null;
}

const typeOptions = ref([
  { label: 'Full time (FT)', value: 'Full time (FT)' },
  { label: 'Part time (PT)', value: 'Part time (PT)' },
  { label: 'Distance Learning (DL)', value: 'Distance Learning (DL)' },
  { label: 'Add more type...', value: '__add_more__' },
]);

const showAddTypeInput = ref(false);
const newType = ref('');

const canAddProgramme = computed(() =>
  !!departmentId.value
  && !!form.program_name.trim()
  && !!form.prog_code.trim()
  && !!form.type.trim()
  && form.duration !== null
  && form.min_units !== null
  && !!form.degree.trim() && !registrationUnitLimitError(form),
);

function handleTypeChange() {
  if (form.type === '__add_more__') {
    showAddTypeInput.value = true;
    form.type = '';
  } else {
    showAddTypeInput.value = false;
  }
}

function addNewType() {
  if (newType.value.trim()) {
    typeOptions.value.splice(typeOptions.value.length - 1, 0, {
      label: newType.value.trim(),
      value: newType.value.trim(),
    });
    form.type = newType.value.trim();
    newType.value = '';
    showAddTypeInput.value = false;
  }
}

function addProgramme() {
  if (!canAddProgramme.value) return;
  addItem({
    department_id: departmentId.value,
    program_name: form.program_name.trim(),
    prog_code: form.prog_code.trim(),
    type: form.type.trim(),
    duration: form.duration ?? 0,
    degree: form.degree.trim(),
    min_units: form.min_units ?? 0,
    min_units_per_registration: form.min_units_per_registration,
    max_units_per_registration: form.max_units_per_registration,
    calculation_method: form.calculation_method || undefined,
    delivery_mode: form.delivery_mode || undefined,
    module_result_mode: (formUsesModules.value && form.module_result_mode) || undefined,
    progression_type: showProgressionType.value ? form.progression_type : undefined,
    outstanding_mode: form.outstanding_mode,
    compulsory_mode: form.compulsory_mode,
    enforce_prerequisites: form.enforce_prerequisites,
  });
  form.program_name = '';
  form.prog_code = '';
  form.type = '';
  form.duration = null;
  form.degree = '';
  form.min_units = null;
  form.min_units_per_registration = null;
  form.max_units_per_registration = null;
  form.calculation_method = null;
  form.delivery_mode = null;
  form.module_result_mode = null;
  form.progression_type = ProgressionTypeEnum.Level;
  form.outstanding_mode = OutstandingModeEnum.SemesterMatch;
  form.compulsory_mode = CompulsoryModeEnum.Enforced;
  form.enforce_prerequisites = false;
}

function removeProgramme(idx: number) {
  removeItem(idx);
}

async function submitAll() {
  if (!pendingProgrammes.value.length) return;
  clearErrors();
  const result = await submitPending(async(prog) => {
    await createTertiaryProgramme(prog);
  });

  await refetchSchoolProgramme();

  const summary = submissionToast(result, 'programme(s)');
  if (summary) toast.add(summary);
}

// ─── Pagination ──────────────────────────────────────────────────
const rowsPerPage = computed(() => Number(paginatorInfo.value?.perPage) || 10);
const firstRecord = computed(
  () => (((Number(paginatorInfo.value?.currentPage) || 1) - 1) * rowsPerPage.value) || 0,
);

async function onPageChange(event: { first: number; rows: number; page?: number }) {
  if (event.rows !== rowsPerPage.value) {
    await setItemsPerPage(event.rows);
  } else if (typeof event.page === 'number') {
    await setPage(event.page + 1);
  } else {
    await setPage(Math.floor(event.first / event.rows) + 1);
  }
}

// ─── Edit ────────────────────────────────────────────────────────
const showEdit = ref(false);
const editingId = ref<string | null>(null);
const editFacultyId = ref<string>('');
const editForm = reactive({
  min_units_per_registration: null as number | null,
  max_units_per_registration: null as number | null,
  program_name: '',
  prog_code: '',
  degree: '',
  type: '',
  duration: 0 as number | null,
  min_units: 0 as number | null,
  calculation_method: null as string | null,
  delivery_mode: ProgrammeDeliveryModeEnum.Regular as ProgrammeDeliveryModeEnum,
  module_result_mode: null as ModuleResultModeEnum | null,
  department_id: '' as string,
  progression_type: ProgressionTypeEnum.Level as ProgressionTypeEnum,
  outstanding_mode: OutstandingModeEnum.SemesterMatch as OutstandingModeEnum,
  compulsory_mode: CompulsoryModeEnum.Enforced as CompulsoryModeEnum,
  enforce_prerequisites: false,
});

const editDepartmentsList = computed(() => {
  const faculty = faculties.value.find(f => f.id === editFacultyId.value);
  return faculty?.departments ?? [];
});

// Clear department when faculty changes in edit modal
watch(editFacultyId, (newVal, oldVal) => {
  if (oldVal && newVal !== oldVal) {
    editForm.department_id = '';
  }
});

const editUsesModules = computed(() => usesModules(editForm.delivery_mode));

// ─── Graduation Criteria (inside edit dialog) ───────────────────
const { result: presetsResult } = useGetGraduationCriteriaPresetsQuery({ fetchPolicy: 'cache-first' });
const presetOptions = computed(() => presetsResult.value?.graduationCriteriaPresets ?? []);
const selectedPreset = ref<string | null>(null);

const editCriteria = reactive({
  program_completion: true,
  unit_validation: true,
  cgpa_validation: true,
  course_completion: true,
  clearance: true,
  min_cgpa: null as number | null,
  min_weighted_avg: null as number | null,
  min_units: null as number | null,
  grading_type: 'numeric',
});

function loadCriteriaFromJson(criteria: Record<string, unknown> | null) {
  const checks = (criteria?.checks ?? {}) as Record<string, boolean>;
  const thresholds = (criteria?.thresholds ?? {}) as Record<string, number | null>;
  editCriteria.program_completion = checks.program_completion ?? true;
  editCriteria.unit_validation = checks.unit_validation ?? true;
  editCriteria.cgpa_validation = checks.cgpa_validation ?? true;
  editCriteria.course_completion = checks.course_completion ?? true;
  editCriteria.clearance = checks.clearance ?? true;
  editCriteria.min_cgpa = thresholds.min_cgpa ?? null;
  editCriteria.min_weighted_avg = thresholds.min_weighted_avg ?? null;
  editCriteria.min_units = thresholds.min_units ?? null;
  editCriteria.grading_type = (criteria?.grading_type as string) ?? 'numeric';
}

function buildCriteriaJson(): Record<string, unknown> {
  return {
    checks: {
      program_completion: editCriteria.program_completion,
      unit_validation: editCriteria.unit_validation,
      cgpa_validation: editCriteria.cgpa_validation,
      course_completion: editCriteria.course_completion,
      clearance: editCriteria.clearance,
    },
    thresholds: {
      min_cgpa: editCriteria.min_cgpa,
      min_weighted_avg: editCriteria.min_weighted_avg,
      min_units: editCriteria.min_units,
    },
    grading_type: editCriteria.grading_type,
  };
}

function applyPreset() {
  if (!selectedPreset.value) return;
  const preset = presetOptions.value.find((p: Record<string, unknown>) => p.key === selectedPreset.value);
  if (preset?.criteria) {
    loadCriteriaFromJson(preset.criteria as Record<string, unknown>);
  }
}

function openEdit(item: ProgrammeType) {
  clearErrors();
  editingId.value = item.id;
  editForm.program_name = item.program_name || '';
  // The stored code is composed (BASE*DEGREE*TYPE*SCHOOL_ID); only the base
  // segment is editable — sending the composed value back re-appends the suffix.
  editForm.prog_code = baseProgCode(item.prog_code);
  editForm.degree = item.degree || '';
  editForm.type = item.type || '';
  editForm.duration = item.duration ?? null;
  editForm.min_units = item.min_units ?? null;
  editForm.calculation_method = item.calculation_method ?? null;
  editForm.delivery_mode = item.delivery_mode ?? ProgrammeDeliveryModeEnum.Regular;
  editForm.module_result_mode = item.module_result_mode ?? null;
  editForm.min_units_per_registration = item.min_units_per_registration ?? null;
  editForm.max_units_per_registration = item.max_units_per_registration ?? null;
  editForm.progression_type = item.progression_type ?? ProgressionTypeEnum.Level;
  editForm.outstanding_mode = (item as Record<string, unknown>).outstanding_mode as OutstandingModeEnum ?? OutstandingModeEnum.SemesterMatch;
  editForm.compulsory_mode = (item as Record<string, unknown>).compulsory_mode as CompulsoryModeEnum ?? CompulsoryModeEnum.Enforced;
  editForm.enforce_prerequisites = (item as Record<string, unknown>).enforce_prerequisites as boolean ?? false;

  // Pre-populate faculty & department for editing
  editForm.department_id = item.department_id || '';
  const parentFaculty = faculties.value.find(f =>
    f.departments?.some(department => department?.id === item.department_id),
  );
  editFacultyId.value = parentFaculty?.id ?? '';

  loadCriteriaFromJson((item as Record<string, unknown>).graduation_criteria as Record<string, unknown> | null);
  selectedPreset.value = null;
  editTranscriptTemplateId.value = (item as Record<string, unknown>).default_transcript_template_id as string | null ?? null;
  originalTranscriptTemplateId.value = editTranscriptTemplateId.value;
  showEdit.value = true;
}

async function submitEdit() {
  if (!editingId.value || registrationUnitLimitError(editForm)) return;
  clearErrors();
  try {
    const result = await updateTertiaryProgramme(editingId.value, {
      program_name: editForm.program_name.trim(),
      prog_code: editForm.prog_code.trim(),
      degree: editForm.degree.trim(),
      type: editForm.type.trim(),
      duration: editForm.duration ?? undefined,
      min_units: editForm.min_units ?? undefined,
      min_units_per_registration: editForm.min_units_per_registration,
      max_units_per_registration: editForm.max_units_per_registration,
      calculation_method: editForm.calculation_method ?? undefined,
      delivery_mode: editForm.delivery_mode,
      module_result_mode: (editUsesModules.value && editForm.module_result_mode) || undefined,
      // Mirrors addProgramme: when the degree isn't in POSTGRAD_DEGREES the
      // toggle is hidden, so send undefined and let the backend keep the stored
      // value — sending LEVEL here silently reset year-based programmes on any
      // unrelated edit.
      progression_type: showEditProgressionType.value ? editForm.progression_type : undefined,
      outstanding_mode: editForm.outstanding_mode,
      compulsory_mode: editForm.compulsory_mode,
      enforce_prerequisites: editForm.enforce_prerequisites,
      graduation_criteria: buildCriteriaJson(),
      department_id: editForm.department_id || undefined,
    });
    if (result) {
      showEdit.value = false;
      editingId.value = null;
      await refetchSchoolProgramme();
    }
  } catch (err) {
    handleServerErrors(err);
  }
}

// ─── Delete ──────────────────────────────────────────────────────
const deletingId = ref<string | null>(null);

function confirmDelete(item: ProgrammeType) {
  confirm.require({
    message: `Delete "${item.program_name}"? Curriculum and registrations under it may be affected.`,
    header: 'Delete Programme',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      deletingId.value = item.id;
      try {
        const ok = await deleteTertiaryProgramme(item.id);
        if (ok) await refetchSchoolProgramme();
      } finally {
        deletingId.value = null;
      }
    },
  });
}

// ─── Transcript Template Assignment ─────────────────────────────
const { result: transcriptTemplatesResult, loading: loadingTranscriptTemplates } = useQuery(GET_TRANSCRIPT_TEMPLATES);
const transcriptTemplateOptions = computed(() => transcriptTemplatesResult.value?.transcriptTemplates ?? []);
const { mutate: setTranscriptTemplateMutate, loading: savingTranscriptTemplate } = useMutation(SET_PROGRAMME_TRANSCRIPT_TEMPLATE_DEFAULT);

const editTranscriptTemplateId = ref<string | null>(null);
const originalTranscriptTemplateId = ref<string | null>(null);

// Reset transcript template state when edit dialog opens
watch(showEdit, (open) => {
  if (!open) {
    editTranscriptTemplateId.value = null;
    originalTranscriptTemplateId.value = null;
  }
});

async function saveTranscriptTemplate() {
  if (!editingId.value) return;
  try {
    await setTranscriptTemplateMutate({
      programme_id: editingId.value,
      transcript_template_id: editTranscriptTemplateId.value || null,
    });
    originalTranscriptTemplateId.value = editTranscriptTemplateId.value;
    toast.add({
      severity: 'success',
      summary: 'Updated',
      detail: editTranscriptTemplateId.value
        ? 'Default transcript template assigned.'
        : 'Default transcript template cleared.',
      life: 3000,
    });
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to update transcript template';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  }
}
</script>
